import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Page, Tabs } from '@shopify/polaris';
import userRoles from '../../utils/userRoles';
import { useAuthStore } from '../../context/useStore';
import { useUrl } from '../../shared/util/hanldesave';
import OverviewTab from './components/OverviewTab';
import RevenueTab from './components/deep/RevenueTab';
import CostsTab from './components/deep/CostsTab';
import CashflowTab from './components/deep/CashflowTab';
import CashPlanningTab from './components/deep/CashPlanningTab';
import KpiTab from './components/deep/KpiTab';
import DashboardTab from './components/dashboard/DashboardTab';

const tabs = [
  {
    id: 'analytics-dashboard',
    content: 'Dashboard',
    accessibilityLabel: 'Executive dashboard',
    panelID: 'analytics-dashboard-content',
    comp: null // set below, it links to the other tabs
  },
  {
    id: 'analytics-overview',
    content: 'Overview',
    accessibilityLabel: 'Analytics overview',
    panelID: 'analytics-overview-content',
    comp: <OverviewTab />
  },
  {
    id: 'analytics-revenue',
    content: 'Revenue',
    accessibilityLabel: 'Outgoing invoices deep dive',
    panelID: 'analytics-revenue-content',
    comp: <RevenueTab />
  },
  {
    id: 'analytics-costs',
    content: 'Costs',
    accessibilityLabel: 'Incoming invoices deep dive',
    panelID: 'analytics-costs-content',
    comp: <CostsTab />
  },
  {
    id: 'analytics-cashflow',
    content: 'Cashflow',
    accessibilityLabel: 'Open positions and payment behavior',
    panelID: 'analytics-cashflow-content',
    comp: <CashflowTab />
  },
  {
    id: 'analytics-cashplanning',
    content: 'Cash planning',
    accessibilityLabel: 'Bank balance forecast and accuracy tracking',
    panelID: 'analytics-cashplanning-content',
    comp: <CashPlanningTab />
  },
  {
    id: 'analytics-yearly',
    content: 'Yearly report',
    accessibilityLabel: 'Yearly KPI report',
    panelID: 'analytics-yearly-content',
    comp: <KpiTab />
  }
];

const tabUrl = (id) => `/analytics?tab=${tabs.findIndex((tab) => tab.id === id)}`;
tabs[0].comp = <DashboardTab links={{ cashflow: tabUrl('analytics-cashflow'), cashPlanning: tabUrl('analytics-cashplanning') }} />;

const parseTabIndex = (value) => {
  const index = parseInt(value, 10);
  return Number.isInteger(index) && index >= 0 && index < tabs.length ? index : null;
};

export default function AnalyticsPage() {
  const { user } = useAuthStore();
  const url = useUrl();
  const navigate = useNavigate();

  const tabFromUrl = parseTabIndex(url.get('tab'));
  const [selected, setSelected] = useState(tabFromUrl ?? 0);

  // Cards link to other tabs (/analytics?tab=4) and the browser back button changes the url as well:
  // follow the url instead of only reading it once at mount.
  useEffect(() => {
    if (tabFromUrl !== null && tabFromUrl !== selected) setSelected(tabFromUrl);
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = useCallback(
    (selectedTabIndex) => {
      setSelected(selectedTabIndex);
      // Keep other params (like the ?bu= business unit filter) when switching tabs
      const params = new URLSearchParams(window.location.search);
      params.set('tab', selectedTabIndex);
      navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate]
  );

  if (user?.role !== userRoles.AdminFull) return null;

  return (
    <Page fullWidth>
      <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
        <Box paddingBlockStart="400">{tabs[selected].comp}</Box>
      </Tabs>
    </Page>
  );
}
